import { Draft } from 'immer';
import { sum } from 'lodash';
import { CareEnvironment, CarePhase } from '../../appModel';
import { chooseByCarePhase, carePhaseDefList, CarePhaseDef } from '../../CarePhaseDef';
import { isFamily } from '../../isFamily';
import { SupportProviderRatePeriod } from '../../SupportProviderRatePeriod';
import { toObjectMap } from '../../toObjectMap';
import { appliedHealthCareInflation } from '../../appliedHealthCareInflation';
import { calcCarePhaseDerivatives } from './calcCarePhaseDerivatives';
import { calcFutureValue } from '../utils/calcFutureValue';
import { isNullOrUndefined } from '../../isNullOrUndefined';
import { isProfessional } from '../../isProfessional';
import { pickClientCareEnvironmentCosts } from '../../pickClientCareEnvironmentCost';
import { hasSeparateRoomAndBoard } from '../../careEnvironmentCapabilities';
import { calcPhaseCarePlanDirect } from '../funding/projection/phaseCarePlanCalculations';
import { calcMonthlyCareInputs } from '../funding/projection/monthlyCareInputCalculations';
import { computeMonthBasedInflatedPhaseCost } from '../funding/projection/sharedEligibility';
import { resolveUseClaimProjectionEngine } from '../utils/resolveUseClaimProjectionEngine';
import type { ClientDerivativeOptions } from '../calcClientDerivatives';

const FULL_CARE_WEEKLY_HOURS = 168; // 24 hours × 7 days — round-the-clock availability
const WEEKS_PER_MONTH = 52 / 12;

const phaseSumKeys: (keyof SummableSinglePhaseCosts)[] = [
  'phaseCareHoursNeeded',
  'phaseCareMonthsNeeded',
  'phaseCareHoursProvided',
  'phaseCareHoursGap',
  'phaseCarePeriodNeeded',
  'phaseTotalCost',
  'phaseFamilyCareHoursProvided',
  'phaseFamilyCareCost',
  'phaseProfessionalCareHoursProvided',
  'phaseProfessionalShareCost',
  'phaseInflatedProfessionalShareCost',
  'phaseInflatedFamilyCareCost',
  'phaseProfessionalShareCostNoDiscount',
  'phaseFamilyCareCostNoDiscount',
  'phaseInflatedProfessionalShareCostNoDiscount',
  'phaseInflatedFamilyCareCostNoDiscount',
  'phaseInflatedProfessionalShareCostDiscountAmount',
  'phaseRoomAndBoardCost',
  'phaseInflatedRoomAndBoardCost',
  'phaseInflatedTotalCost',
];

export function calcCarePhaseCostDerivatives(
  mutableClient: Draft<Client>,
  options: ClientDerivativeOptions = {},
) {
  const useClaimProjectionEngine = resolveUseClaimProjectionEngine(mutableClient, options);
  calcPhaseCosts(mutableClient, useClaimProjectionEngine);
  calcCarePhaseDerivatives(mutableClient);
  // Phase care plans are built BEFORE the inflated costs: the month-based cost
  // model (engine-gated, below) prices each phase on the ledger's
  // rounded-month clock, which lives on `projectionInputs.phaseCarePlans`.
  // Safe to hoist — `calcPhaseCarePlanDirect` reads only inference /
  // support-provider / care-environment inputs, none of the inflated outputs.
  calcPhaseCarePlans(mutableClient);
  calcInflatedCarePhaseCosts(mutableClient, useClaimProjectionEngine);
  calcMonthlyCareInputs(mutableClient);
  calcFamilyCareHoursRatios(mutableClient);
  calcAllPhaseCosts(mutableClient);
  calcProfessionalSupportProviderAppliedHours(mutableClient);
}

function calcPhaseCosts(mutableClient: Client, useClaimProjectionEngine: boolean) {
  mutableClient.phaseCosts = carePhaseDefList.map(carePhaseDef =>
    calcSinglePhaseCosts(mutableClient, carePhaseDef, useClaimProjectionEngine),
  );
}

function calcSinglePhaseCosts(
  mutableClient: Client,
  carePhaseDef: CarePhaseDef,
  useClaimProjectionEngine: boolean,
): SinglePhaseCosts {
  const { appliedInferenceSet, supportProviderSet } = mutableClient;

  const carePhase = carePhaseDef.value;
  const appliedCareEnvironment = mutableClient.appliedCareEnvironments[carePhase];
  const recommendedCareEnvironment = mutableClient.recommendedCareEnvironments[carePhase];

  const {
    phaseOneCareHoursRatio,
    phaseTwoCareHoursRatio,
    phaseThreeCareHoursRatio,
    phaseOneDurationYearsRatio,
    phaseTwoDurationYearsRatio,
    phaseThreeDurationYearsRatio,
  } = appliedInferenceSet;

  const { supportProviders } = supportProviderSet;

  const familySupportProviders = supportProviders.filter(isFamily);

  const phaseCareHoursRatio = chooseByCarePhase(
    carePhase,
    phaseOneCareHoursRatio,
    phaseTwoCareHoursRatio,
    phaseThreeCareHoursRatio,
  );

  const phaseDurationRatio = chooseByCarePhase(
    carePhase,
    phaseOneDurationYearsRatio,
    phaseTwoDurationYearsRatio,
    phaseThreeDurationYearsRatio,
  );

  const inHomeEnvironment = appliedCareEnvironment === CareEnvironment.home;

  const { phaseCareHoursNeeded: predictedCareHoursNeeded, phaseCareMonthsNeeded, isDurationCustom } = calcPhaseCareNeeded(
    mutableClient,
    phaseCareHoursRatio,
    phaseDurationRatio,
    carePhase,
  );

  const isFullCareHomeOverride =
    carePhase === CarePhase.fullCare && inHomeEnvironment && mutableClient.clientCustomInferences.fullCare247Override === true;
  const phaseCareHoursNeeded = isFullCareHomeOverride
    ? Math.round(FULL_CARE_WEEKLY_HOURS * WEEKS_PER_MONTH * phaseCareMonthsNeeded)
    : predictedCareHoursNeeded;

  const { rateAmount, ratePeriod, originalRateAmount } = pickClientCareEnvironmentCosts(
    mutableClient,
    appliedCareEnvironment,
  );

  const phaseCarePeriodNeeded =
    ratePeriod.toLowerCase() === SupportProviderRatePeriod.hourly.toLowerCase()
      ? phaseCareHoursNeeded
      : phaseCareMonthsNeeded;

  const baseRate = originalRateAmount ?? rateAmount;
  const phaseFamilyCareHoursProvided = calcPhaseCareHoursProvided(
    familySupportProviders,
    carePhase,
  );
  const phaseCareHoursProvided = Math.max(phaseFamilyCareHoursProvided, phaseCareHoursNeeded); // Family can provide a surplus
  const phaseProfessionalCareHoursProvided = Math.max(
    0,
    phaseCareHoursNeeded - phaseFamilyCareHoursProvided,
  );
  const phaseCareHoursGap = phaseCareHoursNeeded - phaseCareHoursProvided; // never a gap anymore, but could be surplus

  // Independent Living hybrid cost split (claim-projection engine only): the
  // care portion is priced like home care (professional hours × the client's
  // home rate) and stays policy-eligible, while the IL facility rate is booked
  // separately as out-of-pocket room & board. Legacy-engine clients keep the
  // flat facility cost, so their numbers are unchanged.
  const useRoomAndBoardSplit =
    useClaimProjectionEngine && hasSeparateRoomAndBoard(appliedCareEnvironment);

  let phaseTotalCost: number;
  let phaseTotalCostNoDiscount: number;
  let phaseProfessionalShareCost: number;
  let phaseProfessionalShareCostNoDiscount: number;
  let phaseFamilyCareCost: number;
  let phaseFamilyCareCostNoDiscount: number;
  let phaseRoomAndBoardCost = 0;

  if (useRoomAndBoardSplit) {
    const homeCareCost = pickClientCareEnvironmentCosts(mutableClient, CareEnvironment.home);
    const careRate = homeCareCost.rateAmount;
    const careBaseRate = homeCareCost.originalRateAmount ?? homeCareCost.rateAmount;

    phaseProfessionalShareCost = phaseProfessionalCareHoursProvided * careRate;
    phaseProfessionalShareCostNoDiscount = phaseProfessionalCareHoursProvided * careBaseRate;
    phaseFamilyCareCost = phaseFamilyCareHoursProvided * careRate;
    phaseFamilyCareCostNoDiscount = phaseFamilyCareHoursProvided * careBaseRate;
    phaseRoomAndBoardCost = phaseCareMonthsNeeded * rateAmount;
    phaseTotalCost = phaseProfessionalShareCost + phaseFamilyCareCost + phaseRoomAndBoardCost;
    phaseTotalCostNoDiscount =
      phaseProfessionalShareCostNoDiscount + phaseFamilyCareCostNoDiscount + phaseRoomAndBoardCost;
  } else {
    phaseTotalCost = phaseCarePeriodNeeded * rateAmount;
    phaseTotalCostNoDiscount = phaseCarePeriodNeeded * baseRate;
    phaseProfessionalShareCost = inHomeEnvironment
      ? phaseProfessionalCareHoursProvided * rateAmount // Note: for in-home rate period is always hourly
      : phaseTotalCost;
    phaseFamilyCareCost = phaseTotalCost - phaseProfessionalShareCost;
    phaseProfessionalShareCostNoDiscount = inHomeEnvironment
      ? phaseProfessionalCareHoursProvided * baseRate
      : phaseTotalCostNoDiscount;
    phaseFamilyCareCostNoDiscount = phaseTotalCostNoDiscount - phaseProfessionalShareCostNoDiscount;
  }

  const carePhaseCosts: SinglePhaseCosts = {
    phaseCareHoursNeeded,
    phaseCareMonthsNeeded,
    phaseCareHoursProvided,
    phaseCareHoursGap,
    phaseCarePeriodNeeded,
    phaseTotalCost,
    phaseProfessionalCareHoursProvided,
    phaseProfessionalShareCost,
    phaseFamilyCareHoursProvided,
    phaseFamilyCareCost,
    phaseRoomAndBoardCost,
    phaseCareHoursRatio,
    phaseDurationRatio,
    carePhase,
    appliedCareEnvironment,
    recommendedCareEnvironment,
    inHomeEnvironment,
    isDurationCustom,
    isFullCareHomeOverride,

    // Filled-in later
    familyCareHoursRatio: 0,
    phaseCareProvidedPercent: 0,
    phaseFamilyProvidedPercent: 0,
    phaseInflatedProfessionalShareCost: 0,
    phaseInflatedFamilyCareCost: 0,
    phaseProfessionalShareCostNoDiscount,
    phaseFamilyCareCostNoDiscount,
    phaseInflatedProfessionalShareCostNoDiscount: 0,
    phaseInflatedFamilyCareCostNoDiscount: 0,
    phaseInflatedProfessionalShareCostDiscountAmount: 0,
    phaseInflatedRoomAndBoardCost: 0,
    phaseInflatedTotalCost: 0,
  };

  calcPhaseCareHoursPercents(carePhaseCosts);

  return carePhaseCosts;
}

type PhaseCareNeeded = {
  phaseCareHoursNeeded: number;
  phaseCareMonthsNeeded: number;
  isDurationCustom: boolean;
};

function calcPhaseCareNeeded(
  mutableClient: Draft<Client>,
  phaseCareHoursRatio: number,
  phaseDurationRatio: number,
  carePhase: CarePhase,
): PhaseCareNeeded {
  const {
    appliedInferenceSet: { ltcDurationYears, totalCareHoursNeeded },
    carePhaseDurationSelections,
  } = mutableClient;

  // care phase is in the past
  if (phaseCareHoursRatio === 0 && phaseDurationRatio === 0) {
    return {
      phaseCareHoursNeeded: 0,
      phaseCareMonthsNeeded: 0,
      isDurationCustom: false,
    };
  }

  // If the user has selected a duration, use that, otherwise calculate it
  const phaseCareMonthsSelected = carePhaseDurationSelections[carePhase];
  const phaseCareMonthsPredicted = Math.max(
    Math.round(ltcDurationYears * phaseDurationRatio * 12),
    1,
  );

  const phaseCareMonthsNeeded = phaseCareMonthsSelected ?? phaseCareMonthsPredicted;

  // If the user has selected a duration, multiply the average care hours per month by that, otherwise use predicted
  const phaseCareHoursPredicted = Math.round(totalCareHoursNeeded * phaseCareHoursRatio);
  const phaseCareHoursPerMonthPredicted = phaseCareHoursPredicted / (phaseCareMonthsPredicted ?? 1);
  const phaseCareHoursNeeded = !isNullOrUndefined(phaseCareMonthsSelected)
    ? phaseCareHoursPerMonthPredicted * phaseCareMonthsSelected
    : phaseCareHoursPredicted;

  const isDurationCustom = phaseCareMonthsNeeded !== phaseCareMonthsPredicted;

  return {
    phaseCareHoursNeeded: Math.round(phaseCareHoursNeeded),
    phaseCareMonthsNeeded: Math.round(phaseCareMonthsNeeded),
    isDurationCustom,
  };
}

function calcInflatedCarePhaseCosts(mutableClient: Client, useMonthBasedCostModel: boolean) {
  const {
    phaseCosts,
    intakeSurvey: { clientHasStartedLtc },
    clientYearsTillPhaseEnd,
  } = mutableClient;

  phaseCosts.forEach(phaseCosts => {
    // past phase
    const { carePhase } = phaseCosts;
    if (clientHasStartedLtc && clientYearsTillPhaseEnd[carePhase] === null) {
      phaseCosts.phaseInflatedProfessionalShareCost = phaseCosts.phaseProfessionalShareCost;
      phaseCosts.phaseInflatedFamilyCareCost = phaseCosts.phaseFamilyCareCost;
      phaseCosts.phaseInflatedProfessionalShareCostNoDiscount =
        phaseCosts.phaseProfessionalShareCostNoDiscount;
      phaseCosts.phaseInflatedFamilyCareCostNoDiscount = phaseCosts.phaseFamilyCareCostNoDiscount;
      phaseCosts.phaseInflatedProfessionalShareCostDiscountAmount =
        phaseCosts.phaseInflatedProfessionalShareCostNoDiscount -
        phaseCosts.phaseInflatedProfessionalShareCost;
      phaseCosts.phaseInflatedRoomAndBoardCost = phaseCosts.phaseRoomAndBoardCost;
      return;
    }

    // Claim-projection-engine cost model: price the phase on the same
    // rounded-month, annually-stepped clock the engine's ledger charges, so
    // the app-wide projected cost equals the ledger's Σ effectiveCareCost and
    // a fully-covering policy reads 100% coverage. The closed form below
    // inflates the whole phase cost to the phase-END year — systematically
    // higher for future phases. Legacy-engine clients keep the closed form so
    // their numbers don't shift under them.
    if (useMonthBasedCostModel) {
      phaseCosts.phaseInflatedProfessionalShareCost = computeMonthBasedInflatedPhaseCost(
        mutableClient,
        carePhase,
        phaseCosts.phaseProfessionalShareCost,
      );
      phaseCosts.phaseInflatedFamilyCareCost = computeMonthBasedInflatedPhaseCost(
        mutableClient,
        carePhase,
        phaseCosts.phaseFamilyCareCost,
      );
      phaseCosts.phaseInflatedProfessionalShareCostNoDiscount =
        computeMonthBasedInflatedPhaseCost(
          mutableClient,
          carePhase,
          phaseCosts.phaseProfessionalShareCostNoDiscount,
        );
      phaseCosts.phaseInflatedFamilyCareCostNoDiscount = computeMonthBasedInflatedPhaseCost(
        mutableClient,
        carePhase,
        phaseCosts.phaseFamilyCareCostNoDiscount,
      );
      phaseCosts.phaseInflatedRoomAndBoardCost = computeMonthBasedInflatedPhaseCost(
        mutableClient,
        carePhase,
        phaseCosts.phaseRoomAndBoardCost,
      );
      phaseCosts.phaseInflatedProfessionalShareCostDiscountAmount =
        phaseCosts.phaseInflatedProfessionalShareCostNoDiscount -
        phaseCosts.phaseInflatedProfessionalShareCost;
      // Keep the displayed growth factor consistent with the model that
      // actually priced the phase: with a nominal of 1 this is the average of
      // the phase's per-month stepped factors, so the UI equation
      // `nominal × factor = inflated` multiplies out exactly. The closed-form
      // factor from calcCarePhaseDerivatives anchors on the phase END year and
      // would overstate the month-based costs it no longer produces.
      mutableClient.clientPhaseInflationFactors[carePhase] =
        computeMonthBasedInflatedPhaseCost(mutableClient, carePhase, 1);
      return;
    }

    const appliedHealthCareInflationRate = appliedHealthCareInflation(mutableClient);
    // P1-7: `clientYearsTillPhaseEnd` keeps its TRUE signed value, so clamp the
    // inflation exponent at >= 0 here (mirroring clientPhaseInflationFactors) —
    // a phase ending before the anchor year must not DEFLATE the cost dollars.
    const inflationYears = Math.max(clientYearsTillPhaseEnd[carePhase] ?? 0, 0);
    phaseCosts.phaseInflatedProfessionalShareCost = calcFutureValue(
      phaseCosts.phaseProfessionalShareCost,
      appliedHealthCareInflationRate,
      inflationYears,
    );
    phaseCosts.phaseInflatedFamilyCareCost = calcFutureValue(
      phaseCosts.phaseFamilyCareCost,
      appliedHealthCareInflationRate,
      inflationYears,
    );
    phaseCosts.phaseInflatedProfessionalShareCostNoDiscount = calcFutureValue(
      phaseCosts.phaseProfessionalShareCostNoDiscount,
      appliedHealthCareInflationRate,
      inflationYears,
    );
    phaseCosts.phaseInflatedFamilyCareCostNoDiscount = calcFutureValue(
      phaseCosts.phaseFamilyCareCostNoDiscount,
      appliedHealthCareInflationRate,
      inflationYears,
    );
    phaseCosts.phaseInflatedRoomAndBoardCost = calcFutureValue(
      phaseCosts.phaseRoomAndBoardCost,
      appliedHealthCareInflationRate,
      inflationYears,
    );
    phaseCosts.phaseInflatedProfessionalShareCostDiscountAmount =
      phaseCosts.phaseInflatedProfessionalShareCostNoDiscount -
      phaseCosts.phaseInflatedProfessionalShareCost;
  });

  // Client-facing inflated total = policy-eligible care + out-of-pocket room &
  // board (2-term; the family-care offset stays separate). Room & board is 0
  // outside Independent Living, so this equals the professional share for every
  // other phase. Set after the branch loop so all three inflation paths
  // (past / month-based / closed-form) are covered by one expression.
  phaseCosts.forEach(pc => {
    pc.phaseInflatedTotalCost =
      pc.phaseInflatedProfessionalShareCost + pc.phaseInflatedRoomAndBoardCost;
  });
}

function calcPhaseCareHoursProvided(supportProviders: SupportProvider[], carePhase: CarePhase) {
  return supportProviders.reduce(
    (hoursProvided, supportProvider) =>
      hoursProvided +
      chooseByCarePhase(
        carePhase,
        supportProvider.supportProviderAppliedPhaseOneHours,
        supportProvider.supportProviderAppliedPhaseTwoHours,
        supportProvider.supportProviderAppliedPhaseThreeHours,
      ),
    0,
  );
}

function calcFamilyCareHoursRatios(mutableClient: Client) {
  const { phaseCosts } = mutableClient;
  const sumHomeEnvironmentRatios = sum(
    phaseCosts.map(({ phaseCareHoursRatio, inHomeEnvironment }) =>
      inHomeEnvironment ? phaseCareHoursRatio : 0,
    ),
  );

  phaseCosts.forEach(phaseCosts => {
    phaseCosts.familyCareHoursRatio = phaseCosts.inHomeEnvironment
      ? phaseCosts.phaseCareHoursRatio / sumHomeEnvironmentRatios
      : 0;
  });
}

function calcAllPhaseCosts(mutableClient: Client) {
  const { phaseCosts } = mutableClient;

  const aggregateEachPhaseCosts = phaseCosts.reduce(
    (sumPhaseCosts, singlePhaseCosts) => {
      phaseSumKeys.forEach(
        key => (sumPhaseCosts[key] = (sumPhaseCosts[key] ?? 0) + singlePhaseCosts[key]),
      );
      return sumPhaseCosts;
    },
    {} as SummableSinglePhaseCosts & RecalculateSinglePhaseCosts,
  );

  calcPhaseCareHoursPercents(aggregateEachPhaseCosts);

  const allPhaseCosts = (mutableClient.allPhaseCosts = toObjectMap(
    Object.entries(aggregateEachPhaseCosts),
    ([key]) => `allP${key.substring(1)}`,
    ([, value]) => value,
  ) as unknown as AllPhaseCosts);

  allPhaseCosts.allPhaseHomeCareHoursProvided = 0;
  allPhaseCosts.allPhaseHomeProfessionalCareHoursProvided = 0;
  allPhaseCosts.allPhaseHomeCareHoursNeeded = 0;

  phaseCosts
    .filter(({ inHomeEnvironment }) => inHomeEnvironment)
    .forEach(
      ({ phaseCareHoursNeeded, phaseCareHoursProvided, phaseProfessionalCareHoursProvided }) => {
        allPhaseCosts.allPhaseHomeCareHoursNeeded += phaseCareHoursNeeded;
        allPhaseCosts.allPhaseHomeCareHoursProvided += phaseCareHoursProvided;
        allPhaseCosts.allPhaseHomeProfessionalCareHoursProvided +=
          phaseProfessionalCareHoursProvided;
      },
    );
}

function calcPhaseCareHoursPercents(costs: SummableSinglePhaseCosts & RecalculateSinglePhaseCosts) {
  const { phaseCareHoursNeeded, phaseCareHoursProvided, phaseFamilyCareHoursProvided } = costs;

  costs.phaseCareProvidedPercent = phaseCareHoursNeeded
    ? Math.min(1, phaseCareHoursProvided / phaseCareHoursNeeded)
    : 1;

  costs.phaseFamilyProvidedPercent = Math.min(
    1,
    phaseFamilyCareHoursProvided / phaseCareHoursProvided,
  );
}

function calcProfessionalSupportProviderAppliedHours(mutableClient: Client) {
  const [
    { phaseProfessionalCareHoursProvided: supportProviderAppliedPhaseOneHours },
    { phaseProfessionalCareHoursProvided: supportProviderAppliedPhaseTwoHours },
    { phaseProfessionalCareHoursProvided: supportProviderAppliedPhaseThreeHours },
  ] = mutableClient.phaseCosts;

  const [professionalSupportProvider, ...otherProfessionalSupportProviders] =
    mutableClient.supportProviderSet.supportProviders.filter(isProfessional);

  professionalSupportProvider.supportProviderAllPhaseCareHours =
    (professionalSupportProvider.supportProviderAppliedPhaseOneHours =
      supportProviderAppliedPhaseOneHours) +
    (professionalSupportProvider.supportProviderAppliedPhaseTwoHours =
      supportProviderAppliedPhaseTwoHours) +
    (professionalSupportProvider.supportProviderAppliedPhaseThreeHours =
      supportProviderAppliedPhaseThreeHours);

  // we don't really support multiple professional support providers right now, so for safety blanking them out.
  otherProfessionalSupportProviders.forEach(otherProfessionalSupportProvider => {
    otherProfessionalSupportProvider.supportProviderAllPhaseCareHours = 0;
    otherProfessionalSupportProvider.supportProviderAppliedPhaseOneHours = 0;
    otherProfessionalSupportProvider.supportProviderAppliedPhaseTwoHours = 0;
    otherProfessionalSupportProvider.supportProviderAppliedPhaseThreeHours = 0;
  });
}

function calcPhaseCarePlans(mutableClient: Draft<Client>) {
  const phaseCarePlans = carePhaseDefList.map(carePhaseDef =>
    calcPhaseCarePlanDirect(mutableClient, carePhaseDef),
  );

  const sumHomeEnvironmentRatios = sum(
    phaseCarePlans.map(plan => (plan.inHomeEnvironment ? plan.phaseCareHoursRatio : 0)),
  );

  phaseCarePlans.forEach(plan => {
    plan.familyCareHoursRatio = plan.inHomeEnvironment
      ? plan.phaseCareHoursRatio / sumHomeEnvironmentRatios
      : 0;
  });

  mutableClient.projectionInputs = {
    phaseCarePlans,
    monthlyCareInputs: [],
  };
}
